class BaseComponent extends HTMLElement {
  constructor() {
    super();
    this.dependencies = [];
  }

  connectedCallback() {
    this.loadDependencies().then(() => {
      this.init();
    }).catch(err => {
      console.error(`Failed to load dependencies for ${this.tagName.toLowerCase()}:`, err);
    });
  }

  async loadDependencies() {
    // 1. Gather dependencies from data attributes
    // format: data-dependency-name="Swiper" data-dependency-url="..."
    // We can support multiple by convention or just specific ones. 
    // For now, let's look for generic `data-dependency-start`.
    // Actually, a cleaner way is to let the child class define dependencies or read specific data attrs.
    // The plan said: "Reads data-dependency-name and data-dependency-url"

    // Let's support a standard way:
    // data-resources='[{"name": "Swiper", "url": "...", "type": "script"}, {"url": "...", "type": "style"}]'
    // OR just looking for specific attributes passed by the child.

    // Implementation Plan says: "Reads data-dependency-name and data-dependency-url"
    const name = this.dataset.dependencyName;
    const url = this.dataset.dependencyUrl;
    const cssUrl = this.dataset.dependencyCss;

    const promises = [];

    if (cssUrl) {
      promises.push(this.loadCSS(cssUrl));
    }

    if (name && url) {
      if (!window[name]) {
        promises.push(this.loadScript(url));
      }
    }

    await Promise.all(promises);
  }

  loadCSS(url) {
    if (document.querySelector(`link[href="${url}"]`)) return Promise.resolve();
    return new Promise((resolve, reject) => {
      const link = document.createElement('link');
      link.rel = 'stylesheet';
      link.href = url;
      link.onload = resolve;
      link.onerror = reject;
      document.head.appendChild(link);
    });
  }

  loadScript(url) {
    // Check if script is already loading but not yet window[name]
    // A simple way is to check src.
    if (document.querySelector(`script[src="${url}"]`)) {
      // Ideally we wait for it. For now, let's just create a new one or assume it handles itself?
      // Better: simple global generic loader lock.
      // For this simple implementation, let's just append. 
      // Browser deduping usually handles src check but 'onload' might not fire if already loaded.
      // Let's do a simple check.
      return new Promise((resolve) => {
        // If already exists, we might need to poll for the window object or just resolve (assuming previous loader handles it)
        // For robustness:
        const existing = document.querySelector(`script[src="${url}"]`);
        if (existing) {
          existing.addEventListener('load', resolve); // If already loaded this won't fire.
          // Fallback:
          if (existing.dataset.loaded === 'true') resolve();
          return;
        }

        const script = document.createElement('script');
        script.src = url;
        script.async = true;
        script.dataset.loaded = 'false';
        script.onload = () => {
          script.dataset.loaded = 'true';
          resolve();
        };
        script.onerror = () => {
          // resolve anyway to try continuing? or reject.
          console.error(`Error loading ${url}`);
          resolve();
        };
        document.head.appendChild(script);
      });
    }

    return new Promise((resolve, reject) => {
      const script = document.createElement('script');
      script.src = url;
      script.async = true;
      script.onload = resolve;
      script.onerror = reject;
      document.head.appendChild(script);
    });
  }

  // Child classes should override this
  init() {
    console.warn(`${this.tagName} init() method not implemented.`);
  }

  disconnectedCallback() {
    // Cleanup hook
  }
}

window.BaseComponent = BaseComponent;
